/**
 * Shape of Data generator (6.DPSR.1.3).
 *
 * Choice-based reasoning about distribution shape and which measure best
 * describes the center. A scenario is chosen first and its correct answer is
 * derived from that scenario, so the graded choice always matches the wording.
 *
 * Families:
 *   F1: which measure of center best fits the described shape (median vs mean)
 *   F2: identify the skew / symmetry of a described distribution
 *   F3: the effect of an outlier on a measure of center
 */

import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "data-shape" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

const DIFF: Record<Difficulty, readonly Family[]> = {
  easy: ["F1", "F2"],
  standard: ["F1", "F2", "F3"],
  hard: ["F1", "F2", "F3"],
  challenge: ["F1", "F2", "F3"],
};

/** F1 scenarios: choose between mean and median as the better center. */
interface CenterScenario {
  readonly text: string;
  /** "median" when the data has outliers/skew; "mean" when symmetric. */
  readonly correct: "median" | "mean";
}

const CENTER_SCENARIOS: readonly CenterScenario[] = [
  {
    text: "A data set has a few very large outliers that pull the average up.",
    correct: "median",
  },
  {
    text: "House prices in a neighborhood include one mansion far above the rest.",
    correct: "median",
  },
  {
    text: "Test scores are spread evenly and symmetrically around the middle.",
    correct: "mean",
  },
  {
    text: "Daily temperatures cluster symmetrically with no extreme values.",
    correct: "mean",
  },
  {
    text: "Most incomes are similar but one salary is extremely high.",
    correct: "median",
  },
];

/** F2 scenarios: identify the shape. */
interface ShapeScenario {
  readonly text: string;
  readonly correct: "left" | "right" | "symmetric";
}

const SHAPE_SCENARIOS: readonly ShapeScenario[] = [
  {
    text: "Most values pile up on the left with a long tail stretching to the right.",
    correct: "right",
  },
  {
    text: "Most values pile up on the right with a long tail stretching to the left.",
    correct: "left",
  },
  {
    text: "The data is balanced, with roughly equal tails on both sides of the center.",
    correct: "symmetric",
  },
  {
    text: "A histogram is a mirror image about its center peak.",
    correct: "symmetric",
  },
  {
    text: "A few unusually large values create a long tail on the high end.",
    correct: "right",
  },
];

/** F3 scenarios: the effect of an outlier on a measure. */
interface OutlierScenario {
  readonly text: string;
  /** Which measure is most affected / least affected as asked. */
  readonly correct: "mean" | "median";
}

const OUTLIER_SCENARIOS: readonly OutlierScenario[] = [
  {
    text: "One extreme value is added to a data set. Which measure of center changes the most?",
    correct: "mean",
  },
  {
    text: "A data set gains a single very large outlier. Which measure of center is most resistant to it?",
    correct: "median",
  },
];

export class DataShapeGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const family = rng.pick(DIFF[ctx.difficulty]);

      let promptText: string;
      let choices: Choice[];
      let correctId: string;
      let step: string;

      if (family === "F1") {
        const sc = rng.pick(CENTER_SCENARIOS);
        promptText = `${sc.text} Which measure of center best describes this data?`;
        choices = [
          { id: "mean", label: "Mean" },
          { id: "median", label: "Median" },
        ];
        correctId = sc.correct;
        step =
          sc.correct === "median"
            ? "Outliers and skew distort the mean, so the median better describes the center."
            : "With symmetric data and no outliers, the mean is a reliable center.";
      } else if (family === "F2") {
        const sc = rng.pick(SHAPE_SCENARIOS);
        promptText = `${sc.text} What is the shape of this distribution?`;
        choices = [
          { id: "left", label: "Skewed left" },
          { id: "right", label: "Skewed right" },
          { id: "symmetric", label: "Symmetric" },
        ];
        correctId = sc.correct;
        step =
          sc.correct === "symmetric"
            ? "Balanced tails on both sides mean the distribution is symmetric."
            : `A long tail on the ${sc.correct === "right" ? "high (right)" : "low (left)"} side means the data is skewed ${sc.correct}.`;
      } else {
        const sc = rng.pick(OUTLIER_SCENARIOS);
        promptText = sc.text;
        choices = [
          { id: "mean", label: "Mean" },
          { id: "median", label: "Median" },
        ];
        correctId = sc.correct;
        step =
          sc.correct === "mean"
            ? "The mean uses every value, so an outlier shifts it noticeably."
            : "The median depends only on position, so an outlier barely moves it.";
      }

      // Order is fixed and ids are unique per family; verify the correct id is present.
      const ids = choices.map((c) => c.id);
      if (!ids.includes(correctId)) continue;

      const shuffled = rng.shuffle(choices);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [{ description: step, latex: "\\text{shape}" }],
        hints: [
          { level: 1, text: "Think about whether extreme values or a tail are present." },
          { level: 2, text: "Outliers pull the mean; the median stays near the middle." },
        ],
        subskills: topic.subskills,
        choices: shuffled,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const dataShape = new DataShapeGenerator();
